import { asApiFailure } from "@utils/apiFailure";
import { failedChangeMessage } from "@utils/failedChangeMessage";

const NOT_CHANGED = "The client's status was not changed.";

// A soft-deleted client: every organization write answers 404. The detail
// page shows this in place of the client (review finding 3).
export const CLIENT_GONE_MESSAGE =
    "This client no longer exists. It may have been deleted.";

// The fallback for every failure this page does not explain itself. The
// status PUT still toasts those (useOrganizationApi's predicate suppresses
// only the four statuses below), so the server's own words are on screen.
const GENERIC_FAILURE = failedChangeMessage(NOT_CHANGED);

// The validator's own sentences from a 400's ValidationProblemDetails body
// (`errors`: property -> messages). Only non-blank strings are taken; any
// other shape yields none, and the caller says something plainer.
const validationMessages = (detail: string | undefined): string[] => {
    if (!detail) {
        return [];
    }

    try {
        const parsed: unknown = JSON.parse(detail);
        if (typeof parsed !== "object" || parsed === null) {
            return [];
        }

        const { errors } = parsed as { errors?: unknown };
        if (typeof errors !== "object" || errors === null) {
            return [];
        }

        return Object.values(errors)
            .flatMap((messages: unknown) =>
                Array.isArray(messages) ? messages : [],
            )
            .filter(
                (message: unknown): message is string =>
                    typeof message === "string" && message.trim() !== "",
            );
    } catch {
        return [];
    }
};

// Inline copy for a refused status change. `reloaded` says whether the page
// managed to reload the client after the failure: only then may a 409 say the
// page is current (review finding 6).
//
// Both 409s read the same: the page only offers the transitions its loaded
// status allows, so a refusal means another admin moved the client first,
// whether the API caught it as a lost race (#460) or as a transition the new
// status no longer allows.
export const statusChangeErrorMessage = (
    error: unknown,
    reloaded: boolean,
): string => {
    const failure = asApiFailure(error);

    switch (failure?.status) {
        case 409:
            return reloaded
                ? "Another admin changed this client's status. The page now shows the current status."
                : "Another admin changed this client's status, and the page couldn't load the new status. Reload the page before trying again.";

        case 404:
            return CLIENT_GONE_MESSAGE;

        case 403:
            return "You don't have permission to change this client's status.";

        // The dialog already refuses a blank cancel reason and one over 500,
        // so a 400 here is a rule this page doesn't know; the validator says
        // which.
        case 400: {
            const messages = validationMessages(failure.detail);
            return messages.length > 0
                ? `${NOT_CHANGED} ${messages.join(" ")}`
                : `${NOT_CHANGED} The request was refused as invalid.`;
        }

        default:
            return GENERIC_FAILURE;
    }
};
